import type { ComponentProps, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils/cn";

import { Button, type ButtonProps } from "./button";
import { Card } from "./card";
import { uiKpi } from "./tokens";

export function ListToolbar({ className, ...props }: ComponentProps<"section">) {
  return <section aria-label="List controls" className={cn("flex min-w-0 flex-wrap items-center gap-ds-2 sm:flex-nowrap", className)} {...props} />;
}

export function ListStatGrid({ className, ...props }: ComponentProps<"div">) {
  return <div className={cn("grid gap-ds-3 sm:grid-cols-2 xl:grid-cols-4", className)} {...props} />;
}

export type ListStatTileProps = { title: string; value: string; hint: string; className?: string; valueClassName?: string };
export function ListStatTile({ title, value, hint, className, valueClassName }: ListStatTileProps) {
  return <Card variant="flat" className={cn("border-accent/20 bg-accent/10 px-ds-4 py-ds-4", className)}>
    <div className={uiKpi.label}>{title}</div><div className={cn("mt-ds-2", uiKpi.value, valueClassName)}>{value}</div><div className={cn("mt-ds-2", uiKpi.hint)}>{hint}</div>
  </Card>;
}

export type ListToolbarGroupProps = ComponentProps<"div"> & { grow?: boolean };
export function ListToolbarGroup({ className, grow, ...props }: ListToolbarGroupProps) {
  return <div className={cn("flex min-w-0 flex-wrap items-center gap-ds-2 sm:flex-nowrap", grow ? "flex-1 basis-full sm:basis-auto" : "shrink-0", className)} {...props} />;
}

export type ToolbarIconButtonProps = Omit<ButtonProps, "size" | "children"> & { icon: LucideIcon; label: string; iconClassName?: string };
export function ToolbarIconButton({ icon: Icon, label, className, iconClassName, variant = "secondary", ...props }: ToolbarIconButtonProps) {
  return <Button type="button" variant={variant} size="icon" title={label} aria-label={label} className={cn("shrink-0", className)} {...props}><Icon className={cn("h-4 w-4", iconClassName)} aria-hidden /></Button>;
}

export type ListCountProps = { total: number; noun: string; filtered?: number; filteredLabel?: string; align?: "left" | "right"; className?: string };
export function ListCount({ total, noun, filtered, filteredLabel, align = "left", className }: ListCountProps) {
  const isFiltered = typeof filtered === "number" && filtered !== total;
  return <div className={cn("min-w-0", align === "right" ? "text-right" : "text-left", className)}><p className="text-caption text-muted">
    <span className="font-medium text-foreground">{isFiltered ? filtered : total}</span> {noun}{isFiltered ? <> · {filteredLabel ?? "Filtered"} of {total}</> : null}
  </p></div>;
}

export type ListSurfaceProps = ComponentProps<typeof Card>;
export function ListSurface({ className, ...props }: ListSurfaceProps) {
  return <Card padding="none" className={cn("list-surface min-w-0 overflow-hidden", className)} {...props} />;
}

export type ListSurfaceHeaderProps = ComponentProps<"div"> & { title?: ReactNode; description?: ReactNode; controls?: ReactNode; extra?: ReactNode; meta?: ReactNode };
export function ListSurfaceHeader({ className, title, description, controls, extra, meta, children, ...props }: ListSurfaceHeaderProps) {
  if (children) return <div className={cn("flex flex-col gap-ds-2 border-b border-[var(--card-surface-border)] bg-[var(--surface-card-elevated)] px-ds-4 py-ds-3 sm:flex-row sm:items-start sm:justify-between", className)} {...props}>{children}</div>;
  return <div className={cn("border-b border-[var(--card-surface-border)]", className)} {...props}>
    <div className="flex flex-col gap-ds-3 bg-[var(--surface-card-elevated)] px-ds-4 py-ds-3 lg:flex-row lg:items-center">
      <div className="min-w-0">{title ? <h2 className="truncate text-title text-foreground">{title}</h2> : null}{description ? <p className="mt-ds-1 text-caption text-muted">{description}</p> : null}{meta ? <div className="mt-ds-1 text-caption text-muted">{meta}</div> : null}</div>
      {controls ? <div className="min-w-0 lg:ml-auto">{controls}</div> : null}
    </div>
    {extra ? <div className="flex flex-wrap items-center gap-ds-2 border-t border-[var(--card-surface-border)] bg-[var(--surface-card-inset)] px-ds-4 py-ds-3">{extra}</div> : null}
  </div>;
}

export function ListSurfaceBody({ className, ...props }: ComponentProps<"div">) { return <div className={cn("min-w-0", className)} {...props} />; }

export type ListSurfaceFooterProps = ComponentProps<"div"> & { left?: ReactNode; right?: ReactNode };
export function ListSurfaceFooter({ className, left, right, children, ...props }: ListSurfaceFooterProps) {
  return <div className={cn("flex flex-col gap-ds-3 border-t border-[var(--card-surface-border)] bg-[var(--surface-card-elevated)] px-ds-4 py-ds-4 sm:flex-row sm:items-center sm:justify-between", className)} {...props}>
    {children ?? <><div className="min-w-0 text-caption text-muted">{left}</div><div className="flex flex-wrap items-center gap-ds-2 sm:ml-auto sm:justify-end">{right}</div></>}
  </div>;
}
